import { FFmpeg } from "@ffmpeg/ffmpeg";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
} from "@heroicons/react/16/solid";
import { ChangeEvent, DragEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { useNavigationBlocker } from "../hooks";
import { usePostMaterialMutation } from "../services/materialApi.ts";
import { showToast } from "../slices/statusSlice.ts";
import { loadFfmpeg, transcode } from "../utils/ffmpeg.ts";

export interface FileUploadAreaProps {
  acceptedFileFormat?: string;
}

function FileUploadArea(props: FileUploadAreaProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const collapseCheckboxRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [fileUploadStatus, setFileUploadStatus] = useState<
    Record<string, boolean>
  >({});
  const [confirmCopyright, setConfirmCopyright] = useState(false);

  const [hasEverUploaded, setHasEverUploaded] = useState(false);

  const { courseId } = useParams();
  const { t } = useTranslation(["coursePage"]);

  const [postMaterial] = usePostMaterialMutation();

  const [isProcessingMaterial, setIsProcessingMaterial] = useState(false);

  useNavigationBlocker(isProcessingMaterial);

  const handleUploadMaterial = async (file: File) => {
    try {
      await postMaterial({ courseId: courseId ?? "", file }).unwrap();
      setFileUploadStatus((prev) => ({ ...prev, [file.name]: true }));
    } catch (_err) {
      setFileUploadStatus((prev) => ({ ...prev, [file.name]: false }));
    }
  };

  const handleFileType = async (file: File) => {
    const ffmpeg = new FFmpeg();
    let fileToUpload = file;

    if (file.type.includes("video") || file.type.includes("audio")) {
      await loadFfmpeg(ffmpeg, (progress) =>
        setProgress((prev) => ({ ...prev, [file.name]: progress })),
      );
      fileToUpload = await transcode(ffmpeg, file);
      ffmpeg.terminate();
    }

    await handleUploadMaterial(fileToUpload);
    setProgress((prev) => ({ ...prev, [file.name]: 100 }));
  };

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) {
      return;
    }

    if (collapseCheckboxRef.current) {
      collapseCheckboxRef.current.checked = true;
    }

    await callOnFileUpload(files);
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!confirmCopyright) {
      return;
    }
    setIsDragging(false);
    const files = event.dataTransfer.files;
    await callOnFileUpload(files);
  };

  const callOnFileUpload = async (files: FileList) => {
    setIsProcessingMaterial(true);
    setHasEverUploaded(true);
    for (const file of files) {
      setProgress((prev) => ({ ...prev, [file.name]: 0 }));
    }
    for (const file of files) {
      try {
        await handleFileType(file);
      } catch (_err) {
        dispatch(
          showToast({
            message: t("coursePage.errorMaterialProcessing", {
              fileName: file.name,
              ns: "toast",
            }),
            type: "error",
          }),
        );
      }
    }
    resetFileInput();
    setIsProcessingMaterial(false);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent) => {
    const rect = dropRef?.current?.getBoundingClientRect();
    if (
      rect &&
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    ) {
      return;
    }
    setIsDragging(false);
  };

  return (
    <div className="w-full flex-col gap-2 sm:flex-row">
      <div className="mb-3 flex items-center">
        <input
          id="confirmCopyright"
          type="checkbox"
          className="checkbox checkbox-sm mr-md"
          onChange={(e) => setConfirmCopyright(e.target.checked)}
        />
        <label htmlFor="confirmCopyright" className="cursor-pointer">
          {t("copyrightConfirmation")}
        </label>
      </div>
      <div
        className={`${!confirmCopyright ? "pointer-events-none opacity-50" : ""}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        ref={dropRef}
      >
        <label
          className={`btn btn-neutral h-30 w-full rounded-md p-5 shadow-sm transition focus:outline-none ${
            isDragging ? "bg-primary/10" : ""
          }`}
        >
          <span className="flex h-full w-full cursor-pointer items-center justify-center rounded-md border-2 border-primary border-dashed opacity-50 focus:outline-none">
            <CloudArrowUpIcon className="m-1 h-6 w-6" />
            <span className="m-1 font-semibold">
              {t("materialUploadButton")}
            </span>
          </span>
          <input
            disabled={!confirmCopyright}
            type="file"
            accept={props.acceptedFileFormat || "*/*"}
            name="file_upload"
            className="hidden"
            onChange={handleFileUpload}
            ref={fileInputRef}
            multiple
          />
        </label>
      </div>

      <div
        className={`collapse-arrow collapse mt-5 rounded-box border bg-neutral ${!hasEverUploaded ? "hidden" : ""}`}
      >
        <input type="checkbox" ref={collapseCheckboxRef} />
        <div className="collapse-title flex flex-row justify-between font-medium text-md">
          {isProcessingMaterial ? (
            <ArrowPathIcon className="ml-sm h-6 w-6 animate-spin" />
          ) : (
            <>
              <span className="w-4/5">{t("materialUploadComplete")}</span>
              <CheckCircleIcon className="ml-sm h-6 w-6 text-success" />
            </>
          )}
        </div>
        <div className="collapse-content">
          {Object.entries(progress).map(([fileName, prog]) => (
            <div key={fileName} className="w-full flex-1 flex-col gap-x-md">
              <div key={fileName} className="mb-2 flex items-center">
                <span>{fileName}</span>
              </div>
              <div className="flex flex-row items-center">
                <progress
                  className="progress mr-2 w-full"
                  value={prog}
                  max="100"
                />
                <div className="h-6 w-6">
                  {fileUploadStatus[fileName] && (
                    <CheckCircleIcon className="h-6 w-6 text-success" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default FileUploadArea;
